/**
 * Supabase Client Configuration
 *
 * Initializes and exports the Supabase client for authentication,
 * database queries, and real-time subscriptions.
 */

import { createClient } from '@supabase/supabase-js';
import type { Database } from './supabase-types';

// Get environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    '⚠️ Supabase credentials not configured. Cloud features (auth, sync, sharing) will be disabled.'
  );
}

/**
 * Supabase client instance
 *
 * Features:
 * - Authentication (email/password, magic links, OAuth)
 * - Database queries with Row Level Security
 * - Real-time subscriptions
 * - File storage
 */
export const supabase = createClient<Database>(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storage: localStorage,
    },
    db: {
      schema: 'public',
    },
    global: {
      headers: {
        'X-Client-Info': 'mapquizzr-v2',
      },
    },
  }
);

/**
 * Check if Supabase is properly configured
 */
export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey);

/**
 * Helper to check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  if (!isSupabaseConfigured) return false;

  const { data: { session } } = await supabase.auth.getSession();
  return !!session;
}

/**
 * Helper to get current user
 */
export async function getCurrentUser() {
  if (!isSupabaseConfigured) return null;

  const { data: { user } } = await supabase.auth.getUser();
  return user;
}

/**
 * Helper to sign out
 */
export async function signOut() {
  if (!isSupabaseConfigured) return;

  await supabase.auth.signOut();
}
